import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fileUrl, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  CheckCircle2, Circle, FileText, Video, FileType2, Award, ArrowLeft, ArrowRight,
  Lock, AlertTriangle, Package,
} from "lucide-react";
import { toast } from "sonner";
import VoiceOver from "@/components/VoiceOver";

const ICONS = { video: Video, text: FileText, pdf: FileType2, scorm: Package };
const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

export default function LearnPage() {
  const { token } = useParams();
  const [session, setSession] = useState(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const [view, setView] = useState("welcome"); // welcome | module | quiz | result
  const [quizAnswers, setQuizAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/candidate/session?token=${token}`)
      .then((r) => {
        setSession(r.data);
        if (r.data.progress?.status === "completed") {
          setView("result");
          setResult({
            score_pct: r.data.progress.quiz_score,
            passed: true,
            status: "completed",
          });
        }
      })
      .catch((e) => setError(formatApiError(e.response?.data?.detail, "Invalid or expired link")));
  }, [token]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8">
        <div className="text-center max-w-md">
          <AlertTriangle size={32} strokeWidth={1.2} className="mx-auto text-destructive" />
          <h1 className="mt-6 text-2xl font-black tracking-tighter">Link not available</h1>
          <p className="mt-3 text-sm text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  if (!session) {
    return <div className="min-h-screen flex items-center justify-center font-mono text-sm text-muted-foreground">Loading course…</div>;
  }

  const { candidate, course, branding } = session;
  const isTest = !!session.is_test;
  const modules = course.modules || [];
  const viewed = new Set(session.progress?.viewed_modules || []);
  const allViewed = modules.every((m) => viewed.has(m.id));
  const hasQuiz = (course.quiz_questions || []).length > 0;

  const markViewed = async (moduleId) => {
    if (viewed.has(moduleId)) return;
    try {
      const r = await api.post(`/candidate/progress?token=${token}`, { module_id: moduleId });
      setSession((s) => ({ ...s, progress: { ...s.progress, viewed_modules: r.data.viewed_modules } }));
    } catch { /* ignore */ }
  };

  const onScormResult = (moduleId, resp) => {
    // Update local session with server truth
    setSession((s) => ({
      ...s,
      progress: {
        ...s.progress,
        viewed_modules: resp.viewed_modules || s.progress.viewed_modules,
        status: resp.status || s.progress.status,
        completed_at: resp.completed_at || s.progress.completed_at,
      },
    }));
    if (resp.status === "completed") {
      setResult({ score_pct: null, passed: true, status: "completed" });
      setView("result");
      toast.success("Course completed!");
    } else if (resp.passed) {
      toast.success("Module completed");
    }
  };

  const startCourse = () => {
    setView("module");
    setActiveIdx(0);
  };
  const goToModule = (i) => {
    setActiveIdx(i);
    setView("module");
    const m = modules[i];
    if (m && m.type !== "scorm") markViewed(m.id);
  };
  const goQuiz = () => setView("quiz");

  const submitQuiz = async () => {
    const answers = (course.quiz_questions || []).map((_, i) => quizAnswers[i] ?? -1);
    if (answers.some((a) => a < 0)) {
      toast.error("Please answer every question");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post(`/candidate/quiz?token=${token}`, { answers });
      setResult(r.data);
      setView("result");
      if (r.data.passed && r.data.status === "completed") toast.success("Course completed!");
      else if (!r.data.passed) toast.error(`Score ${r.data.score_pct}% — need ${r.data.pass_pct_required}%`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  const retryQuiz = () => { setQuizAnswers({}); setResult(null); setView("quiz"); };

  const activeModule = modules[activeIdx];

  if (view === "welcome") {
    return (
      <div className="min-h-screen bg-background">
        {isTest && <TestModeBanner />}
        <header className="border-b border-border">
          <div className="max-w-5xl mx-auto px-6 md:px-12 h-16 flex items-center gap-3">
            {branding?.logo_path ? (
              <img src={fileUrl(branding.logo_path)} alt={branding.company_name} className="h-8 object-contain" />
            ) : (
              <div className="h-7 w-7" style={{ backgroundColor: branding?.primary_color || "#002e7a" }} />
            )}
            <span className="font-mono text-sm tracking-tight">{branding?.company_name || "Early Years Academy"}</span>
          </div>
        </header>

        <main className="max-w-5xl mx-auto px-6 md:px-12 py-20 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <div className="eyebrow mb-4">/ CPD course · {course.cpd_hours}h</div>
            <h1 className="text-4xl md:text-5xl font-black tracking-tighter leading-[0.95]">{course.title}</h1>
            <p className="mt-6 text-base text-muted-foreground leading-relaxed max-w-xl">
              {course.description || "Welcome — this CPD course is ready for you."}
            </p>
            <div className="mt-10 border border-border p-6 bg-secondary">
              <div className="eyebrow mb-2">/ Welcome</div>
              <div className="text-lg font-semibold">{candidate.name}</div>
              <div className="font-mono text-xs text-muted-foreground">{candidate.email}</div>
            </div>
            <div className="mt-8 grid grid-cols-3 gap-4 font-mono text-xs">
              <div className="border border-border p-4">
                <div className="text-muted-foreground">Modules</div>
                <div className="mt-2 text-xl font-bold">{modules.length}</div>
              </div>
              <div className="border border-border p-4">
                <div className="text-muted-foreground">Questions</div>
                <div className="mt-2 text-xl font-bold">{(course.quiz_questions || []).length}</div>
              </div>
              <div className="border border-border p-4">
                <div className="text-muted-foreground">Pass mark</div>
                <div className="mt-2 text-xl font-bold">{course.quiz_pass_pct}%</div>
              </div>
            </div>
            <Button
              onClick={startCourse}
              data-testid="start-course-button"
              className="mt-10 rounded-sm h-12 px-8"
              style={{ backgroundColor: branding?.primary_color || "#002e7a", color: "#fff" }}
            >
              Start course <ArrowRight size={16} strokeWidth={1.5} className="ml-2" />
            </Button>
          </div>
          <aside className="lg:col-span-5">
            <div className="border border-border p-6 sticky top-8">
              <div className="eyebrow mb-4">/ What you&apos;ll do</div>
              <ol className="space-y-3 text-sm">
                <li className="flex items-start gap-3"><span className="font-mono text-muted-foreground">01</span> Work through {modules.length} module{modules.length === 1 ? "" : "s"} at your own pace.</li>
                {hasQuiz && <li className="flex items-start gap-3"><span className="font-mono text-muted-foreground">02</span> Pass the final quiz with {course.quiz_pass_pct}% or higher.</li>}
                <li className="flex items-start gap-3"><span className="font-mono text-muted-foreground">{hasQuiz ? "03" : "02"}</span> Download your branded CPD certificate.</li>
              </ol>
            </div>
          </aside>
        </main>
      </div>
    );
  }

  if (view === "result") {
    const passed = result?.passed && result?.status === "completed";
    return (
      <div className="min-h-screen bg-background">
        {isTest && <TestModeBanner />}
        <header className="border-b border-border">
          <div className="max-w-4xl mx-auto px-6 md:px-12 h-16 flex items-center gap-3">
            {branding?.logo_path ? (
              <img src={fileUrl(branding.logo_path)} alt="" className="h-8 object-contain" />
            ) : (
              <div className="h-7 w-7" style={{ backgroundColor: branding?.primary_color || "#002e7a" }} />
            )}
            <span className="font-mono text-sm tracking-tight">{branding?.company_name}</span>
          </div>
        </header>
        <main className="max-w-3xl mx-auto px-6 md:px-12 py-20 text-center">
          {passed ? (
            <>
              <div className="inline-flex items-center justify-center h-20 w-20 bg-foreground text-background mb-8">
                <Award size={40} strokeWidth={1.2} />
              </div>
              <div className="eyebrow mb-3">/ Congratulations</div>
              <h1 className="text-5xl font-black tracking-tighter">You passed.</h1>
              <p className="mt-4 text-muted-foreground max-w-md mx-auto">
                You completed <span className="font-bold text-foreground">{course.title}</span>. Your certificate is ready.
              </p>
              <Link to={`/certificate/${token}`}>
                <Button
                  data-testid="view-certificate-button"
                  className="mt-10 rounded-sm h-12 px-8"
                  style={{ backgroundColor: branding?.primary_color || "#002e7a", color: "#fff" }}
                >
                  View certificate <ArrowRight size={16} strokeWidth={1.5} className="ml-2" />
                </Button>
              </Link>
            </>
          ) : (
            <>
              <div className="inline-flex items-center justify-center h-20 w-20 bg-destructive text-destructive-foreground mb-8">
                <AlertTriangle size={40} strokeWidth={1.2} />
              </div>
              <h1 className="text-4xl font-black tracking-tighter">Not quite there</h1>
              <p className="mt-4 text-muted-foreground max-w-md mx-auto">
                You scored <span className="font-bold text-foreground">{result?.score_pct}%</span>. Need <span className="font-bold text-foreground">{result?.pass_pct_required}%</span> to pass. Review the modules and try the quiz again.
              </p>
              <div className="mt-10 flex justify-center gap-3">
                <Button onClick={() => setView("module")} variant="outline" data-testid="review-modules-button" className="rounded-sm h-12 px-6">
                  <ArrowLeft size={16} strokeWidth={1.5} className="mr-2" /> Review modules
                </Button>
                <Button onClick={retryQuiz} data-testid="retake-quiz-button" className="rounded-sm h-12 px-6 bg-foreground text-background">
                  Retake quiz
                </Button>
              </div>
            </>
          )}
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {isTest && <TestModeBanner />}
      <div className="grid grid-cols-1 lg:grid-cols-12">
        <aside className="lg:col-span-3 border-r border-border bg-secondary lg:h-screen lg:sticky lg:top-0 overflow-y-auto">
          <div className="p-6 border-b border-border">
            <div className="flex items-center gap-3">
              {branding?.logo_path ? (
                <img src={fileUrl(branding.logo_path)} alt="" className="h-8 object-contain" />
              ) : (
                <div className="h-6 w-6" style={{ backgroundColor: branding?.primary_color || "#002e7a" }} />
              )}
              <span className="font-mono text-xs tracking-tight">{branding?.company_name}</span>
            </div>
            <h2 className="mt-4 text-base font-semibold tracking-tight leading-tight">{course.title}</h2>
            <div className="mt-2 font-mono text-[10px] tracking-widest uppercase text-muted-foreground">
              {viewed.size} / {modules.length} viewed
            </div>
          </div>
          <nav className="p-3">
            {modules.map((m, i) => {
              const Icon = ICONS[m.type] || FileText;
              const isViewed = viewed.has(m.id);
              const isActive = view === "module" && activeIdx === i;
              return (
                <button
                  key={m.id}
                  data-testid={`sidebar-module-${i}`}
                  onClick={() => goToModule(i)}
                  className={`w-full text-left flex items-start gap-3 px-3 py-2.5 text-sm rounded-sm transition-colors ${
                    isActive ? "bg-foreground text-background" : "hover:bg-background"
                  }`}
                >
                  <span className="font-mono text-[10px] w-5 pt-1">{String(i + 1).padStart(2, "0")}</span>
                  <Icon size={14} strokeWidth={1.5} className="mt-1 shrink-0" />
                  <span className="flex-1 leading-snug">{m.title || "Untitled"}</span>
                  {isViewed ? <CheckCircle2 size={14} className={isActive ? "text-background" : "text-foreground"} /> : <Circle size={14} className="opacity-30" />}
                </button>
              );
            })}
            {hasQuiz && (
              <button
                onClick={goQuiz}
                data-testid="sidebar-quiz-button"
                disabled={!allViewed}
                className={`mt-4 w-full text-left flex items-center gap-3 px-3 py-2.5 text-sm rounded-sm border transition-colors ${
                  view === "quiz"
                    ? "bg-foreground text-background border-foreground"
                    : allViewed
                      ? "border-border hover:bg-background"
                      : "border-border opacity-50 cursor-not-allowed"
                }`}
              >
                {allViewed ? <Award size={14} strokeWidth={1.5} /> : <Lock size={14} strokeWidth={1.5} />}
                <span className="font-semibold">Final quiz</span>
                <span className="ml-auto font-mono text-[10px] text-muted-foreground">{(course.quiz_questions || []).length} Q</span>
              </button>
            )}
          </nav>
        </aside>

        <section className={`lg:col-span-9 ${activeModule?.type === "scorm" ? "" : "p-6 md:p-12 lg:p-16 max-w-4xl"}`}>
          {view === "module" && activeModule && (
            <ModuleView
              module={activeModule}
              index={activeIdx}
              total={modules.length}
              onNext={() => {
                // Text/video/pdf modules mark viewed on Next.
                // SCORM modules normally rely on the SCO's LMSFinish, but if the
                // package is missing we mark viewed here so the candidate isn't
                // stuck — the module can't drive its own progress.
                const shouldMarkViewed = activeModule.type !== "scorm" || activeModule.package_missing;
                // Compute the effective viewed set locally — the async markViewed
                // + React state update wouldn't be visible on this render, so a
                // last-module Next click would otherwise fail to open the quiz.
                const effective = shouldMarkViewed
                  ? new Set([...viewed, activeModule.id])
                  : viewed;
                if (shouldMarkViewed) markViewed(activeModule.id);
                if (activeIdx < modules.length - 1) {
                  setActiveIdx(activeIdx + 1);
                } else if (hasQuiz && modules.every((m) => effective.has(m.id))) {
                  setView("quiz");
                }
              }}
              onPrev={() => activeIdx > 0 && setActiveIdx(activeIdx - 1)}
              onMount={() => { if (activeModule.type !== "scorm") markViewed(activeModule.id); }}
              hasQuiz={hasQuiz}
              canStartQuiz={hasQuiz && allViewed}
              accent={branding?.primary_color || "#002e7a"}
              token={token}
              voiceRate={branding?.voice_rate ?? 1}
              voicePitch={branding?.voice_pitch ?? 1}
              onScormResult={onScormResult}
            />
          )}
          {view === "quiz" && (
            <QuizView
              questions={course.quiz_questions || []}
              answers={quizAnswers}
              setAnswers={setQuizAnswers}
              onSubmit={submitQuiz}
              submitting={submitting}
              passPct={course.quiz_pass_pct}
              accent={branding?.primary_color || "#002e7a"}
            />
          )}
        </section>
      </div>
    </div>
  );
}

function TestModeBanner() {
  return (
    <div data-testid="test-mode-banner" className="sticky top-0 z-40 bg-yellow-100 text-yellow-900 border-b border-yellow-300 px-6 py-2 flex items-center justify-center gap-3 text-sm">
      <span className="inline-block w-2 h-2 bg-yellow-500 rounded-full animate-pulse" />
      <span className="font-mono text-[11px] tracking-[0.2em] uppercase font-bold">Preview / Test mode</span>
      <span className="hidden sm:inline text-xs">— Progress saved to a sandbox invitation; not counted in dashboard stats.</span>
    </div>
  );
}

function ModuleView({ module, index, total, onNext, onPrev, onMount, hasQuiz, canStartQuiz, accent, token, voiceRate, voicePitch, onScormResult }) {
  useEffect(() => {
    if (onMount) onMount();
    try { window.speechSynthesis.cancel(); } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [module.id]);

  const supportsVoice = module.type === "text" || module.type === "pdf";
  const hasProAudio = !!module.audio_path;

  const fetchModuleText = async () => {
    if (module.type === "text") return module.text_content || "";
    const r = await api.get(`/candidate/module-text?token=${token}&module_id=${module.id}`);
    return r.data?.text || "";
  };

  if (module.type === "scorm") {
    if (module.package_missing) {
      return (
        <ScormMissingCard
          module={module}
          index={index}
          total={total}
          onPrev={onPrev}
          onNext={onNext}
          hasQuiz={hasQuiz}
          accent={accent}
        />
      );
    }
    return (
      <ScormPlayer
        module={module}
        token={token}
        onScormResult={onScormResult}
        onNext={onNext}
        onPrev={onPrev}
        index={index}
        total={total}
        hasQuiz={hasQuiz}
        canStartQuiz={canStartQuiz}
        accent={accent}
      />
    );
  }

  return (
    <div className="relative">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="font-mono text-xs tracking-widest text-muted-foreground uppercase">
            Module {String(index + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
          </div>
          <h1 className="mt-3 text-3xl md:text-4xl font-black tracking-tighter">{module.title || "Untitled"}</h1>
        </div>
        {supportsVoice && !hasProAudio && (
          <VoiceOver getText={fetchModuleText} defaultRate={voiceRate} defaultPitch={voicePitch} accent={accent} />
        )}
      </div>

      {hasProAudio && (
        <div className="mt-6 p-4 border border-border bg-secondary flex items-center gap-4">
          <div className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground shrink-0">
            / Audio narration
          </div>
          <audio data-testid={`module-pro-audio-${index}`} src={fileUrl(module.audio_path)} controls preload="metadata" className="flex-1" />
        </div>
      )}

      <div className="mt-10">
        {module.type === "video" && module.video_url && (
          <div className="aspect-video bg-black border border-border overflow-hidden">
            {/\.(mp4|webm|ogg)$/i.test(module.video_url) ? (
              <video src={module.video_url} controls className="w-full h-full" />
            ) : (
              <iframe src={module.video_url} title={module.title} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="w-full h-full" />
            )}
          </div>
        )}
        {module.type === "text" && (
          <article className="prose max-w-none text-base leading-relaxed">
            {module.image_path && (
              <figure className="mb-6">
                <img src={fileUrl(module.image_path)} alt={module.image_caption || module.title} className="w-full max-h-96 object-cover border border-border" />
                {module.image_caption && (
                  <figcaption className="mt-2 font-mono text-[10px] tracking-widest uppercase text-muted-foreground">{module.image_caption}</figcaption>
                )}
              </figure>
            )}
            <div className="whitespace-pre-wrap">{module.text_content || "No content provided."}</div>
          </article>
        )}
        {module.type === "pdf" && module.pdf_path && (
          <div className="space-y-3">
            <object data-testid={`pdf-frame-${index}`} data={fileUrl(module.pdf_path)} type="application/pdf" className="w-full h-[78vh] border border-border bg-secondary">
              <iframe src={fileUrl(module.pdf_path)} title={module.title} className="w-full h-[78vh] border border-border bg-secondary" />
            </object>
            <div className="flex items-center justify-between flex-wrap gap-3 text-xs font-mono text-muted-foreground">
              <span>If the slides don&apos;t appear above, open them in a new tab:</span>
              <a href={fileUrl(module.pdf_path)} target="_blank" rel="noopener noreferrer" data-testid={`pdf-open-newtab-${index}`} className="underline hover:text-foreground">
                Open slides in new tab ↗
              </a>
            </div>
          </div>
        )}
      </div>

      <div className="mt-12 flex items-center justify-between border-t border-border pt-6">
        <Button onClick={onPrev} variant="outline" data-testid="module-prev-button" disabled={index === 0} className="rounded-sm h-11">
          <ArrowLeft size={16} strokeWidth={1.5} className="mr-2" /> Previous
        </Button>
        {(() => {
          const isLast = index === total - 1;
          const showQuizCta = isLast && hasQuiz;
          const quizLocked = showQuizCta && !canStartQuiz;
          const label = isLast
            ? (hasQuiz ? (quizLocked ? "Finish this module to unlock the quiz" : "Start quiz") : "Finish")
            : "Next module";
          return (
            <Button
              onClick={onNext}
              disabled={quizLocked}
              data-testid="module-next-button"
              className="rounded-sm h-11 px-6"
              style={quizLocked ? undefined : { backgroundColor: accent, color: "#fff" }}
            >
              {quizLocked && <Lock size={14} strokeWidth={1.5} className="mr-2" />}
              {label}
              {!quizLocked && <ArrowRight size={16} strokeWidth={1.5} className="ml-2" />}
            </Button>
          );
        })()}
      </div>
    </div>
  );
}

function ScormPlayer({ module, token, onScormResult, onNext, onPrev, index, total, hasQuiz, canStartQuiz, accent }) {
  const iframeRef = useRef(null);
  const [status, setStatus] = useState({ initialized: false, lesson_status: "not attempted", score: null });
  const [ready, setReady] = useState(false);

  // Persist CMI state across re-renders — otherwise every parent state change
  // (e.g. a viewed_modules update from us) would wipe the SCO's session.
  const stateRef = useRef({
    lesson_status: "not attempted",
    completion_status: "unknown",
    success_status: "unknown",
    score_raw: null,
    score_max: 100,
    score_min: 0,
    score_scaled: null,
    location: "",
    total_time: "0000:00:00.00",
    suspend_data: "",
    exit: "",
    last_error: "0",
  });

  // Route the latest onScormResult through a ref so the API bridge effect
  // does NOT depend on it — the parent recreates the function every render
  // and we want the API mounted exactly once per module.
  const onScormResultRef = useRef(onScormResult);
  useEffect(() => { onScormResultRef.current = onScormResult; });

  // ---- Hydrate saved CMI from the server before the iframe loads ----
  useEffect(() => {
    let cancelled = false;
    setReady(false);
    stateRef.current = {
      ...stateRef.current,
      lesson_status: "not attempted",
      completion_status: "unknown",
      success_status: "unknown",
      score_raw: null, score_scaled: null,
      location: "", suspend_data: "", total_time: "0000:00:00.00",
    };
    (async () => {
      try {
        const r = await api.get(`/candidate/scorm-state?token=${token}&module_id=${module.id}`);
        if (cancelled) return;
        const saved = r.data || {};
        stateRef.current = {
          ...stateRef.current,
          lesson_status: saved.lesson_status || stateRef.current.lesson_status,
          completion_status: saved.completion_status || stateRef.current.completion_status,
          success_status: saved.success_status || stateRef.current.success_status,
          score_raw: saved.score_raw ?? stateRef.current.score_raw,
          score_scaled: saved.score_scaled ?? stateRef.current.score_scaled,
          location: saved.location || stateRef.current.location,
          total_time: saved.total_time || stateRef.current.total_time,
          suspend_data: saved.suspend_data || stateRef.current.suspend_data,
        };
        setStatus({
          initialized: true,
          lesson_status: stateRef.current.lesson_status || stateRef.current.completion_status,
          score: stateRef.current.score_raw ?? (stateRef.current.score_scaled != null ? Math.round(stateRef.current.score_scaled * 100) : null),
        });
      } catch { /* first-time visit — no saved state yet */ }
      if (!cancelled) setReady(true);
    })();
    return () => { cancelled = true; };
  }, [module.id, token]);

  // ---- Mount the SCORM API bridges — ONCE per module ----
  useEffect(() => {
    if (!ready) return undefined;
    const state = stateRef.current;

    const reportToServer = async () => {
      try {
        const resp = await api.post(`/candidate/scorm-progress?token=${token}`, {
          module_id: module.id,
          lesson_status: state.lesson_status,
          completion_status: state.completion_status,
          success_status: state.success_status,
          score_raw: state.score_raw,
          score_scaled: state.score_scaled,
          location: state.location,
          total_time: state.total_time,
          suspend_data: state.suspend_data,
        });
        onScormResultRef.current?.(module.id, resp.data);
        setStatus({
          initialized: true,
          lesson_status: state.lesson_status || state.completion_status,
          score: state.score_raw ?? (state.score_scaled != null ? Math.round(state.score_scaled * 100) : null),
        });
      } catch { /* ignore transient errors */ }
    };

    // ---- SCORM 1.2 API ----
    const API_12 = {
      LMSInitialize: (_p) => { state.last_error = "0"; return "true"; },
      LMSFinish: (_p) => { reportToServer(); state.last_error = "0"; return "true"; },
      LMSCommit: (_p) => { reportToServer(); state.last_error = "0"; return "true"; },
      LMSGetValue: (name) => {
        state.last_error = "0";
        if (name === "cmi.core.lesson_status") return state.lesson_status;
        if (name === "cmi.core.student_id") return "candidate";
        if (name === "cmi.core.student_name") return "Candidate";
        if (name === "cmi.core.lesson_location") return state.location;
        if (name === "cmi.suspend_data") return state.suspend_data;
        if (name === "cmi.core.score.raw") return state.score_raw != null ? String(state.score_raw) : "";
        if (name === "cmi.core.score.min") return String(state.score_min);
        if (name === "cmi.core.score.max") return String(state.score_max);
        if (name === "cmi.core.total_time") return state.total_time;
        if (name === "cmi.core.entry") return state.lesson_status === "not attempted" ? "ab-initio" : "resume";
        if (name === "cmi.core.credit") return "credit";
        if (name === "cmi.core.lesson_mode") return "normal";
        return "";
      },
      LMSSetValue: (name, value) => {
        state.last_error = "0";
        if (name === "cmi.core.lesson_status") state.lesson_status = String(value);
        else if (name === "cmi.core.score.raw") state.score_raw = Number(value);
        else if (name === "cmi.core.score.min") state.score_min = Number(value);
        else if (name === "cmi.core.score.max") state.score_max = Number(value);
        else if (name === "cmi.core.lesson_location") state.location = String(value);
        else if (name === "cmi.suspend_data") state.suspend_data = String(value);
        else if (name === "cmi.core.session_time") state.total_time = String(value);
        else if (name === "cmi.core.exit") state.exit = String(value);
        return "true";
      },
      LMSGetLastError: () => state.last_error,
      LMSGetErrorString: (_c) => "",
      LMSGetDiagnostic: (_c) => "",
    };

    // ---- SCORM 2004 API ----
    const API_2004 = {
      Initialize: (_p) => { state.last_error = "0"; return "true"; },
      Terminate: (_p) => { reportToServer(); state.last_error = "0"; return "true"; },
      Commit: (_p) => { reportToServer(); state.last_error = "0"; return "true"; },
      GetValue: (name) => {
        state.last_error = "0";
        if (name === "cmi.completion_status") return state.completion_status;
        if (name === "cmi.success_status") return state.success_status;
        if (name === "cmi.learner_id") return "candidate";
        if (name === "cmi.learner_name") return "Candidate";
        if (name === "cmi.location") return state.location;
        if (name === "cmi.suspend_data") return state.suspend_data;
        if (name === "cmi.score.raw") return state.score_raw != null ? String(state.score_raw) : "";
        if (name === "cmi.score.min") return String(state.score_min);
        if (name === "cmi.score.max") return String(state.score_max);
        if (name === "cmi.score.scaled") return state.score_scaled != null ? String(state.score_scaled) : "";
        if (name === "cmi.total_time") return state.total_time;
        if (name === "cmi.entry") return state.completion_status === "completed" || state.location ? "resume" : "ab-initio";
        if (name === "cmi.credit") return "credit";
        if (name === "cmi.mode") return "normal";
        return "";
      },
      SetValue: (name, value) => {
        state.last_error = "0";
        if (name === "cmi.completion_status") state.completion_status = String(value);
        else if (name === "cmi.success_status") state.success_status = String(value);
        else if (name === "cmi.score.raw") state.score_raw = Number(value);
        else if (name === "cmi.score.min") state.score_min = Number(value);
        else if (name === "cmi.score.max") state.score_max = Number(value);
        else if (name === "cmi.score.scaled") state.score_scaled = Number(value);
        else if (name === "cmi.location") state.location = String(value);
        else if (name === "cmi.suspend_data") state.suspend_data = String(value);
        else if (name === "cmi.session_time") state.total_time = String(value);
        else if (name === "cmi.exit") state.exit = String(value);
        return "true";
      },
      GetLastError: () => state.last_error,
      GetErrorString: (_c) => "",
      GetDiagnostic: (_c) => "",
    };

    window.API = API_12;
    window.API_1484_11 = API_2004;

    const poll = setInterval(() => {
      if (state.lesson_status !== "not attempted" || state.completion_status === "completed") {
        reportToServer();
      }
    }, 15000);

    const flush = () => reportToServer();
    window.addEventListener("beforeunload", flush);

    return () => {
      clearInterval(poll);
      window.removeEventListener("beforeunload", flush);
      try { delete window.API; } catch { window.API = undefined; }
      try { delete window.API_1484_11; } catch { window.API_1484_11 = undefined; }
    };
  }, [ready, module.id, token]);

  const entryUrl = `${BACKEND}/api/scorm/${module.scorm_package_id}/${module.scorm_entry}`;

  return (
    <div className="flex flex-col h-screen">
      <div className="px-6 py-3 border-b border-border flex items-center justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="font-mono text-[10px] tracking-widest text-muted-foreground uppercase truncate">
            Module {String(index + 1).padStart(2, "0")} / {String(total).padStart(2, "0")} · SCORM {module.scorm_version}
          </div>
          <h1 className="mt-1 text-lg md:text-xl font-black tracking-tight truncate">{module.title || "SCORM Module"}</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span data-testid="scorm-status" className={`inline-flex items-center gap-2 px-2 py-1 font-mono text-[10px] tracking-widest uppercase border ${
            status.lesson_status === "passed" || status.lesson_status === "completed"
              ? "bg-foreground text-background border-foreground"
              : "bg-secondary border-border text-muted-foreground"
          }`}>
            {status.lesson_status}
            {status.score != null && <span>· {status.score}</span>}
          </span>
          <Button onClick={onPrev} variant="outline" size="sm" data-testid="module-prev-button" disabled={index === 0} className="rounded-sm">
            <ArrowLeft size={14} strokeWidth={1.5} className="mr-1" /> Prev
          </Button>
          {(() => {
            const isLast = index === total - 1;
            const showQuizCta = isLast && hasQuiz;
            const quizLocked = showQuizCta && !canStartQuiz;
            const label = isLast
              ? (hasQuiz ? (quizLocked ? "Complete SCO to unlock quiz" : "Start quiz") : "Finish")
              : "Next";
            return (
              <Button
                onClick={onNext}
                disabled={quizLocked}
                size="sm"
                data-testid="module-next-button"
                className="rounded-sm"
                style={quizLocked ? undefined : { backgroundColor: accent, color: "#fff" }}
                title={quizLocked ? "This SCORM module must report passed/completed before the quiz unlocks" : undefined}
              >
                {quizLocked && <Lock size={12} strokeWidth={1.5} className="mr-1" />}
                {label}
                {!quizLocked && <ArrowRight size={14} strokeWidth={1.5} className="ml-1" />}
              </Button>
            );
          })()}
        </div>
      </div>
      {ready ? (
        <iframe
          ref={iframeRef}
          data-testid={`scorm-iframe-${index}`}
          src={entryUrl}
          title={module.title}
          className="flex-1 w-full border-0 bg-white"
          allow="autoplay; fullscreen"
        />
      ) : (
        <div className="flex-1 flex items-center justify-center bg-white text-muted-foreground text-sm font-mono">
          Loading SCORM package…
        </div>
      )}
    </div>
  );
}

function ScormMissingCard({ module, index, total, onPrev, onNext, hasQuiz, accent }) {
  const isLast = index === total - 1;
  return (
    <div className="p-8 md:p-12 max-w-3xl" data-testid={`scorm-missing-${index}`}>
      <div className="font-mono text-[10px] tracking-widest text-muted-foreground uppercase">
        Module {String(index + 1).padStart(2, "0")} / {String(total).padStart(2, "0")} · SCORM {module.scorm_version}
      </div>
      <h1 className="mt-3 text-3xl md:text-4xl font-black tracking-tighter">{module.title || "SCORM Module"}</h1>
      <div className="mt-10 border-2 border-destructive/40 bg-destructive/5 p-6">
        <div className="flex items-start gap-3">
          <AlertTriangle size={22} strokeWidth={1.5} className="text-destructive shrink-0 mt-1" />
          <div>
            <h2 className="text-lg font-semibold tracking-tight text-destructive">This course is temporarily unavailable</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              The SCORM package for this module can&apos;t be found on the server. This usually happens right after a deployment when cached
              files were reset. Please contact your training administrator — they&apos;ll need to re-upload the SCORM package.
            </p>
            <div className="mt-4 font-mono text-[10px] text-muted-foreground">
              Reference: <span className="text-foreground">{module.scorm_package_id}</span>
              {module.scorm_original_filename ? ` · ${module.scorm_original_filename}` : ""}
            </div>
          </div>
        </div>
      </div>
      <div className="mt-8 flex items-center justify-between">
        <Button onClick={onPrev} variant="outline" data-testid="module-prev-button" disabled={index === 0} className="rounded-sm h-11">
          <ArrowLeft size={16} strokeWidth={1.5} className="mr-2" /> Previous
        </Button>
        <Button onClick={onNext} data-testid="module-next-button" className="rounded-sm h-11 px-6" style={{ backgroundColor: accent, color: "#fff" }}>
          {isLast ? (hasQuiz ? "Skip to quiz" : "Finish") : "Skip module"}
          <ArrowRight size={16} strokeWidth={1.5} className="ml-2" />
        </Button>
      </div>
    </div>
  );
}

function QuizView({ questions, answers, setAnswers, onSubmit, submitting, passPct, accent }) {  return (
    <div>
      <div className="font-mono text-xs tracking-widest text-muted-foreground uppercase">Final assessment</div>
      <h1 className="mt-3 text-3xl md:text-4xl font-black tracking-tighter">Quiz</h1>
      <p className="mt-3 text-sm text-muted-foreground">Answer every question. You need {passPct}% or higher to pass.</p>

      <div className="mt-10 space-y-6">
        {questions.map((q, qi) => (
          <div key={q.id || qi} data-testid={`quiz-question-${qi}`} className="border border-border p-6">
            <div className="flex gap-4">
              <span className="font-mono text-xs text-muted-foreground pt-1">Q{String(qi + 1).padStart(2, "0")}</span>
              <div className="flex-1">
                <h3 className="text-lg font-semibold tracking-tight">{q.question}</h3>
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-2">
                  {q.options.map((opt, oi) => {
                    const selected = answers[qi] === oi;
                    return (
                      <label
                        key={oi}
                        data-testid={`quiz-option-${qi}-${oi}`}
                        className={`flex items-center gap-3 border p-3 rounded-sm cursor-pointer transition-colors ${
                          selected ? "border-foreground bg-secondary" : "border-border hover:bg-secondary"
                        }`}
                      >
                        <input type="radio" name={`q-${qi}`} checked={selected} onChange={() => setAnswers({ ...answers, [qi]: oi })} />
                        <span className="font-mono text-xs text-muted-foreground">{String.fromCharCode(65 + oi)}</span>
                        <span className="text-sm">{opt}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-10 flex justify-end">
        <Button onClick={onSubmit} disabled={submitting} data-testid="submit-quiz-button" className="rounded-sm h-12 px-8" style={{ backgroundColor: accent, color: "#fff" }}>
          {submitting ? "Submitting..." : "Submit quiz"} <ArrowRight size={16} strokeWidth={1.5} className="ml-2" />
        </Button>
      </div>
    </div>
  );
}
